import { useMutation } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { putAmendOrderPriceApi } from '@flipster/apis/v2/trade';
import type { PendingPosition } from '@flipster/r230522/original/order';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { TextButton } from '@flipster/react/design-system/text-button';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import type { V2OrderSideType } from '@flipster/utils/types/base';

import { orderAttributionAtom } from '../../order-attribution';
// type-only: 런타임 의존 없음(webview-safe). 사이즈 입력 모드의 정의는 popover UI가 소유한다.
import type { SizeInputMode } from '../../position/adjust-order/popover';
import { getPendingOrderRoles } from '../../position/pending-order-roles';
import { SymbolScope } from '../../symbol/bunja';
import { tradingSessionCheckBunja } from '../../trading-suspension/state';
import { buildTradeRequestBase } from '../base-params';
import { buildSizeParams } from './utils';

interface AmendLimitOrderParams {
  symbol: string;
  orderId: string;
  /** Omit when the user hasn't changed price. */
  price?: string;
  sizeMode: SizeInputMode;
  /** Omit when the user hasn't changed size. */
  sizeValue?: string;
  leverage: number;
}

interface AmendTriggerOrderParams {
  symbol: string;
  orderId: string;
  /** Omit when the user hasn't changed trigger price. */
  triggerPrice?: string;
  side: V2OrderSideType;
  sizeMode: SizeInputMode;
  /** Omit when the user hasn't changed size. */
  sizeValue?: string;
  leverage: number;
}

export type AmendOrderParams = AmendLimitOrderParams | AmendTriggerOrderParams;

/**
 * pending 주문 amend(가격/사이즈 수정) 액션.
 * useMutation(훅) 형태를 유지한다 — 행(row)마다 훅 인스턴스가 생겨 per-row 독립 로딩이 보존된다.
 */
export const useAmendOrder = () => {
  const t = useTranslations();
  const attribution = useAtomValue(orderAttributionAtom);

  const { isPending: isAmendLoading, mutateAsync: amendOrder } = useMutation({
    mutationFn: async (params: AmendOrderParams) => {
      const base = buildTradeRequestBase(attribution);

      if ('side' in params) {
        await putAmendOrderPriceApi(params.symbol, params.orderId, {
          ...base,
          // Per API spec: when triggerPrice is set, side MUST be set to the order's side.
          ...(params.triggerPrice != null && { triggerPrice: params.triggerPrice, side: params.side }),
          ...buildSizeParams(params.sizeMode, params.sizeValue, params.leverage),
        });
      } else {
        await putAmendOrderPriceApi(params.symbol, params.orderId, {
          ...base,
          ...(params.price != null && { price: params.price }),
          ...buildSizeParams(params.sizeMode, params.sizeValue, params.leverage),
        });
      }
    },
    onSuccess: () => {
      snackbar.success({ message: t('Trade.r260408.Order_updated_toast') });
    },
    onError: (error, params) => {
      const errorCode = getErrorCode(error);
      const message = getErrorMessage(error);
      if (errorCode === 'TooHighLeverage') {
        snackbar.error({
          message,
          button: (
            <TextButton
              size='medium'
              label={t('Common.r250401.Learn_more')}
              onClick={() => window.open(`/support/trading-rules/leverage-margin/${params.symbol}`, '_blank')}
            />
          ),
        });
        return;
      }
      snackbar.error({ message });
    },
  });

  return { isAmendLoading, amendOrder };
};

/**
 * 주문 Modify(amend)의 off-hours 알림을 **팝오버/모달을 열기 전에** 띄우는 게이트 — ERD-240 §8.2.
 * (adjust leverage 등 다른 액션과 동일하게 "모달 열기 전" notice로 UX 통일 — 2026-07 디자인 논의 결정.)
 * reduce-only / TP-SL 주문 수정은 alert exempt → 게이트 통과(알림 없이 열림).
 *
 * AdjustOrderPopover의 `onBeforeOpen`에 그대로 넘긴다:
 * - 진행(Proceed) → true → 팝오버 오픈. / 알림 취소(닫기) → false → 미오픈.
 */
export const useOffHoursAmendGuard = (order: PendingPosition): (() => Promise<boolean>) => {
  const { isTpsl } = getPendingOrderRoles(order);
  const { guardOffHoursAlert } = useBunja(tradingSessionCheckBunja, [SymbolScope.bind(order.symbol)]);

  return async () => {
    if (order.isReduceOnly || isTpsl) return true; // exempt: 알림 없이 연다
    return guardOffHoursAlert('orderModify');
  };
};
